/** @format */
import { Context, ReactElement, ReactNode, createContext } from 'react';

import { ModalSize, ModalVariant } from '@toucan/client/global/application';

export type MenuContextProps = {
    visible: boolean;
    content: ReactElement | null;
};

export type BlackoutContextProps = {
    visible: boolean;
};

export type AdditionalHeaderContextProps = {
    start?: number;
    end?: number;
};

export enum ScrollLock {
    MODAL = 'modal',
    TYPEAHEAD = 'typeahead',
    DRAWER = 'drawer',
    TRAY = 'tray',
}

export type ModalContextProps = {
    visible: boolean;
    content: ReactNode;
    size: ModalSize;
    closeLabel: string;
    isClosable: boolean;
    onClose?: () => void;
    variant?: ModalVariant;
};

export type TrayContextProps = {
    visible: boolean;
    content: ReactNode;
    onClose?: () => void;
};

export type LegalLinksContextProps = Record<
    string,
    {
        urlSlug: string;
    }
>;

export type ModalOptions = Partial<
    Pick<
        ModalContextProps,
        'size' | 'closeLabel' | 'isClosable' | 'onClose' | 'variant'
    >
>;

export type ApplicationContextProps = {
    stickyFooterHeight: number;
    setStickyFooterHeight: (arg: number) => void;
    userAgentDetectedAsBot: boolean;
    blackout: BlackoutContextProps;
    modal: ModalContextProps;
    tray: TrayContextProps;
    menu: MenuContextProps;
    additionalHeaderDisplayBoundary: AdditionalHeaderContextProps;
    legalLinks: LegalLinksContextProps;
    setBlackoutVisible: (arg: boolean) => void;
    setMenuVisible: (arg: boolean) => void;
    setModalVisible: (arg: boolean, options?: ModalOptions) => void;
    setModalContent: (arg: ReactNode) => void;
    setModalSize: (arg: ModalSize) => void;
    setModalCloseLabel: (label: string) => void;
    setTrayVisible: (arg: boolean) => void;
    setTrayContent: (arg: ReactNode) => void;
    setMenuContent: (arg: ReactElement) => void;
    addPageScrollLock: (lock: ScrollLock, selectors?: string[]) => void;
    removePageScrollLock: (lock: ScrollLock, selectors?: string[]) => void;
    setAdditionalHeaderDisplayBoundary: ({
        start,
        end,
    }: AdditionalHeaderContextProps) => void;
};

export const ApplicationContext: Context<ApplicationContextProps> =
    createContext({} as ApplicationContextProps);

ApplicationContext.displayName = 'ApplicationContext';
